import { useMemo } from 'react';

import * as navigation from 'app-level/navigation';
import { FIELD_CONFIG, formatWaDtiBps } from 'configs/columns';
import { transformOperand } from 'context/DataContext';
import { filtersToQueryFilters } from 'features/common-elements/filters/filterValuesParsing';
import { GetDealLoans } from 'features/deals/dealCards/gql/__generated__/inquiryQueries';
import { PortfolioSummaryMetrics } from 'features/drilldown/cashflows/gql/cashflowDataFragments';
import { useUserSettings } from 'features/pages/portfolio/Dashboard/PoolManager/utils/useUserSettings';

import { gql, useQuery } from '@apollo/client';

import { AssetClass, Filter, FilterableField, FilterOperator } from '__generated__/globalTypes';
import { GetDealDiligence } from 'query/__generated__/diligence';
import {
  GetPortfolioSummary,
  GetPortfolioSummaryVariables,
} from './__generated__/PortfolioSummary';
import { KPI } from './Dashboard/PoolManager/types';
import { MetricsHeader } from './Portfolio/MetricsHeader';
import { PortfolioFilterConfig } from './Portfolio/portfolioFilters.config';
import { usePortfolioFilters } from './Portfolio/usePortfolioFilters';
import { SelectedPoolData, usePortfolioContext } from './PortfolioContext';

type GetDealLoans_deal_performance_summary = NonNullable<
  GetDealLoans['deal']
>['performance_summary'];
type GetDealDiligence_deal_diligence_performance_summary = NonNullable<
  GetDealDiligence['deal']
>['diligence_performance_summary'];
// user/company are NON_NULL in the schema, so this is equivalent to the more defensively-wrapped
// `NonNullable<NonNullable<NonNullable<GetPortfolioSummary>['user']>['company']>['performanceData']`
// other consumers use -- exported as that same name/shape so this is the one canonical definition.
export type GetPortfolioSummary_user_company_performanceData =
  GetPortfolioSummary['user']['company']['performanceData'];

const createQueryFilters = (assetClass: AssetClass | null, filters: Filter[]): Filter[] => {
  const baseFilters = assetClass
    ? [
        {
          field_name: FilterableField.asset_class,
          operator: FilterOperator.IS,
          operand: `${assetClass}`,
        },
      ]
    : [];

  return [
    ...baseFilters,
    ...filters.map((filter: Filter) => ({
      field_name: filter.field_name,
      operator: filter.operator,
      operand: transformOperand(filter.field_name, filter.operand),
      operandList: filter.operandList ?? null,
    })),
  ];
};

export const GET_PORTFOLIO_SUMMARY_QUERY = gql`
  query GetPortfolioSummary($hasAssetClass: Boolean!, $assetClass: String, $filters: [Filter!]) {
    user {
      id
      company {
        id
        performanceData: performance_summary(filters: []) {
          ...PortfolioSummaryMetrics
        }
        assetClassPerformanceData: performance_summary(
          filters: [{ field_name: asset_class, operator: IS, operand: $assetClass }]
        ) @include(if: $hasAssetClass) {
          ...PortfolioSummaryMetrics
        }
        filteredPerformanceData: performance_summary(filters: $filters) {
          ...PortfolioSummaryMetrics
        }
      }
    }
  }
  ${PortfolioSummaryMetrics.performanceSummaryMetrics}
`;

const DSCR_RATIO_SELECTORS = ['avgDscr', 'minDscr', 'maxDscr'] as const;

const DSCR_POPULATION_SELECTORS = ['dscrCount', 'nonQmCount', 'dscrPct', 'nonQmPct'] as const;

export const DSCR_KPI_SELECTORS = [...DSCR_RATIO_SELECTORS, ...DSCR_POPULATION_SELECTORS] as const;

export const hasDscrPopulation = (
  performanceData: { dscrCount?: number | null } | undefined,
): boolean => {
  const dscrCount = performanceData?.dscrCount;
  return dscrCount != null && dscrCount > 0;
};

// Asset-depletion tiles (ENG-3391). Null assetUtilCount means the analytics
// service hasn't shipped the field yet (nullable in the schema) — treated the
// same as an empty population: tiles hidden, never a fabricated 0.
export const ASSET_UTIL_KPI_SELECTORS = ['assetUtilCount', 'assetUtilPct'] as const;

export const hasAssetUtilPopulation = (
  performanceData: { assetUtilCount?: number | null } | undefined,
): boolean => {
  const count = performanceData?.assetUtilCount;
  return count != null && count > 0;
};

// The cashflow-filtered snapshot: same keys as the real performance-data
// fragment (minus __typename, which isn't a numeric field), every value
// possibly null.
export type PortfolioSummaryNumericSnapshot = Record<
  Exclude<keyof GetPortfolioSummary_user_company_performanceData, '__typename'>,
  number | null
>;

type KpiPerformanceData =
  | GetPortfolioSummary_user_company_performanceData
  | GetDealLoans_deal_performance_summary
  | GetDealDiligence_deal_diligence_performance_summary
  | SelectedPoolData
  | PortfolioSummaryNumericSnapshot;

/** Resolves a KPI raw value without coercing absent DSCR ratios to zero. */
export const resolveKpiValue = (
  performanceData: KpiPerformanceData | undefined,
  selector: Exclude<keyof GetPortfolioSummary_user_company_performanceData, '__typename'>,
): number | null | undefined => {
  if (!performanceData) {
    return undefined;
  }

  const raw = (performanceData as Record<string, number | null | undefined>)[selector];
  const dscrCount = (performanceData as { dscrCount?: number | null }).dscrCount;
  const isDscrSelector =
    (DSCR_RATIO_SELECTORS as readonly string[]).includes(selector) ||
    (DSCR_POPULATION_SELECTORS as readonly string[]).includes(selector);

  if (isDscrSelector) {
    if (dscrCount == null) {
      return null;
    }

    if (dscrCount === 0) {
      if ((DSCR_RATIO_SELECTORS as readonly string[]).includes(selector)) {
        return null;
      }
      return raw ?? 0;
    }

    if ((DSCR_RATIO_SELECTORS as readonly string[]).includes(selector)) {
      return raw ?? null;
    }

    return raw ?? 0;
  }

  // Asset-util counts are nullable in the schema: null means the analytics
  // service predates the field (ENG-3391) and must render absent, never 0 —
  // a 0 would claim the pool has no asset-depletion loans when nothing counted.
  if ((ASSET_UTIL_KPI_SELECTORS as readonly string[]).includes(selector)) {
    return raw ?? null;
  }

  return raw ?? 0;
};

export type KPIConfig = {
  description: string;
  synonyms?: string[];
  selector: Exclude<keyof GetPortfolioSummary_user_company_performanceData, '__typename'>;
  display: (value: number | null | undefined) => string;
};

export const kpiConfigs: KPIConfig[] = [
  {
    description: 'UPB',
    synonyms: ['Unpaid Principal Balance'],
    selector: 'unpaidBalance',
    display: FIELD_CONFIG.original_balance_cents.display as KPIConfig['display'],
  },
  {
    description: '#',
    selector: 'loanCount',
    display: (value) => (value ?? 0).toLocaleString(),
  },
  {
    description: 'WALA',
    synonyms: ['Weighted Average Loan Age'],
    selector: 'averageAge',
    display: (value) => (value ?? 0).toLocaleString(),
  },
  {
    description: 'WAM',
    synonyms: ['Weighted Average Maturity'],
    selector: 'averageMaturity',
    display: (value) => (value ?? 0).toLocaleString(),
  },
  {
    description: 'GWAC',
    selector: 'netCoupon',
    display: FIELD_CONFIG.interest_rate_bps.display as KPIConfig['display'],
  },
  {
    description: 'FICO',
    selector: 'fico',
    display: (value) => (value ?? 0).toLocaleString(),
  },
  {
    description: 'DTI',
    selector: 'dti',
    display: formatWaDtiBps as KPIConfig['display'],
  },
  {
    description: 'LTV',
    selector: 'ltv',
    display: FIELD_CONFIG.ltv_bps.display as KPIConfig['display'],
  },
  {
    description: 'Avg DSCR',
    selector: 'avgDscr',
    display: FIELD_CONFIG.dscr_bps.display as KPIConfig['display'],
  },
  {
    description: 'Min DSCR',
    selector: 'minDscr',
    display: FIELD_CONFIG.dscr_bps.display as KPIConfig['display'],
  },
  {
    description: 'Max DSCR',
    selector: 'maxDscr',
    display: FIELD_CONFIG.dscr_bps.display as KPIConfig['display'],
  },
  {
    description: '# DSCR',
    selector: 'dscrCount',
    display: (value) => (value == null ? '—' : value.toLocaleString()),
  },
  {
    description: '# Non-QM',
    selector: 'nonQmCount',
    display: (value) => (value == null ? '—' : value.toLocaleString()),
  },
  {
    description: '% DSCR',
    selector: 'dscrPct',
    display: (value) => (value == null ? '—' : `${value.toFixed(1)}%`),
  },
  {
    description: '% Non-QM',
    selector: 'nonQmPct',
    display: (value) => (value == null ? '—' : `${value.toFixed(1)}%`),
  },
  {
    description: '# Asset-Depletion',
    selector: 'assetUtilCount',
    display: (value) => (value == null ? '—' : value.toLocaleString()),
  },
  {
    description: '% Asset-Depletion',
    selector: 'assetUtilPct',
    display: (value) => (value == null ? '—' : `${value.toFixed(1)}%`),
  },
];

export const filterVisibleKpiConfigs = (
  performanceData: { dscrCount?: number | null; assetUtilCount?: number | null } | undefined,
): KPIConfig[] => {
  const hidden: string[] = [];
  if (!hasDscrPopulation(performanceData)) {
    hidden.push(...DSCR_KPI_SELECTORS);
  }
  if (!hasAssetUtilPopulation(performanceData)) {
    hidden.push(...ASSET_UTIL_KPI_SELECTORS);
  }
  return hidden.length ? kpiConfigs.filter((kpi) => !hidden.includes(kpi.selector)) : kpiConfigs;
};

export const generateMetrics = (performanceData: KpiPerformanceData | undefined): KPI[] => {
  return kpiConfigs.map((kpi) => {
    const value = resolveKpiValue(performanceData, kpi.selector);
    return {
      ...kpi,
      value: kpi.display(value),
    };
  });
};

export const poolScopedTitle = (base: string, poolName: string): string => `${base}: ${poolName}`;

const PortfolioSummary = () => {
  const { selectedPoolsData, totalFilteredPoolData } = usePortfolioContext();
  const assetClass = navigation.usePortfolioAssetClass();
  const gqlAssetClass = assetClass;
  const [portfolioFilters] = navigation.usePortfolioFilters();
  const filterProps = usePortfolioFilters();
  const queryFilters = filterProps.filtersConfig
    ? filtersToQueryFilters<PortfolioFilterConfig>(portfolioFilters, filterProps.filtersConfig)
    : [];
  const hasAssetClass = Boolean(gqlAssetClass);

  const generalUserSettings = useUserSettings(`pool-manager-performanceData-${gqlAssetClass}`);
  const poolBySelectionSettings = useUserSettings(
    `pool-manager-total-by-selection-${gqlAssetClass}`,
  );
  const assetClassUserSettings = useUserSettings(
    `pool-manager-assetClassPerformanceData-${gqlAssetClass}`,
  );
  const filteredUserSettings = useUserSettings(
    `pool-manager-filteredPerformanceData-${gqlAssetClass}`,
  );

  const { data } = useQuery<GetPortfolioSummary, GetPortfolioSummaryVariables>(
    GET_PORTFOLIO_SUMMARY_QUERY,
    {
      variables: {
        hasAssetClass,
        assetClass: gqlAssetClass,
        filters: createQueryFilters(gqlAssetClass, queryFilters),
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { company } = data?.user || {};

  // Memoized on the actual underlying performance-data reference, not
  // recomputed on every render: each MetricsHeader instance depends on its
  // `metrics` array by reference in a useEffect, and an unmemoized
  // generateMetrics(...) call here (this component re-renders for reasons
  // that have nothing to do with these values — a sibling MetricsHeader's
  // own save/refetch, a filter interaction elsewhere, this query's own
  // poll) handed every instance a fresh array each time regardless.
  // Same failure mode as useUserSettings' userPoolSettings memoization.
  const performanceMetrics = useMemo(
    () => generateMetrics(company?.performanceData),
    [company?.performanceData],
  );
  const assetClassMetrics = useMemo(
    () => generateMetrics(company?.assetClassPerformanceData),
    [company?.assetClassPerformanceData],
  );
  const selectedPoolMetrics = useMemo(
    () => generateMetrics(selectedPoolsData[0]),
    [selectedPoolsData],
  );
  const filteredMetrics = useMemo(
    () => generateMetrics(company?.filteredPerformanceData),
    [company?.filteredPerformanceData],
  );
  const totalFilteredPoolMetrics = useMemo(
    () => generateMetrics(totalFilteredPoolData ?? undefined),
    [totalFilteredPoolData],
  );

  return (
    <div data-tour="stratifications-portfolio-summary">
      <MetricsHeader
        title="Total in Portfolio"
        metrics={performanceMetrics}
        addUserSetting={generalUserSettings.addUserSetting}
        savedPoolSettings={generalUserSettings.savedPoolSettings}
        userPoolSettings={generalUserSettings.userPoolSettings}
      />

      {hasAssetClass && (
        <MetricsHeader
          title="Total in Asset Class"
          metrics={assetClassMetrics}
          addUserSetting={assetClassUserSettings.addUserSetting}
          savedPoolSettings={assetClassUserSettings.savedPoolSettings}
          userPoolSettings={assetClassUserSettings.userPoolSettings}
        />
      )}

      {!!selectedPoolsData.length && (
        <MetricsHeader
          title={poolScopedTitle('Total in Pool', selectedPoolsData[0].name)}
          metrics={selectedPoolMetrics}
          addUserSetting={poolBySelectionSettings.addUserSetting}
          savedPoolSettings={poolBySelectionSettings.savedPoolSettings}
          userPoolSettings={poolBySelectionSettings.userPoolSettings}
        />
      )}

      {queryFilters.length > 0 && !selectedPoolsData.length && (
        <MetricsHeader
          title="Total Filtered"
          metrics={filteredMetrics}
          addUserSetting={filteredUserSettings.addUserSetting}
          savedPoolSettings={filteredUserSettings.savedPoolSettings}
          userPoolSettings={filteredUserSettings.userPoolSettings}
        />
      )}

      {queryFilters.length > 0 && totalFilteredPoolData && !!selectedPoolsData.length && (
        <MetricsHeader
          title={poolScopedTitle('Total Filtered in Pool', selectedPoolsData[0].name)}
          metrics={totalFilteredPoolMetrics}
          addUserSetting={filteredUserSettings.addUserSetting}
          savedPoolSettings={filteredUserSettings.savedPoolSettings}
          userPoolSettings={filteredUserSettings.userPoolSettings}
        />
      )}
    </div>
  );
};

export default PortfolioSummary;
